import React, { useState } from 'react';
import { Layout, Menu, Button, Typography, Drawer, Grid } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CalendarOutlined,
  AppstoreOutlined,
  LogoutOutlined,
  InboxOutlined,
  MenuOutlined,
  StarOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import BookingsManagement from '../components/admin/BookingsManagement';
import CalendarView from '../components/admin/CalendarView';
import PackagesManagement from '../components/admin/PackagesManagement';
import SpecialPackagesManagement from '../components/admin/SpecialPackagesManagement';
import TestimonialsManagement from '../components/admin/TestimonialsManagement';
import GalleryManagement from '../components/admin/GalleryManagement';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

type MenuKey = 'bookings' | 'calendar' | 'packages' | 'special-packages' | 'testimonials' | 'gallery';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [selectedMenu, setSelectedMenu] = useState<MenuKey>('bookings');
  const [mobileMenuVisible, setMobileMenuVisible] = useState<boolean>(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  const handleMenuClick = (key: MenuKey) => {
    setSelectedMenu(key);
    setMobileMenuVisible(false);
  };

  const menuItems = [
    {
      key: 'bookings',
      icon: <InboxOutlined />,
      label: 'Bookings',
    },
    {
      key: 'calendar',
      icon: <CalendarOutlined />,
      label: 'Calendar',
    },
    {
      key: 'packages',
      icon: <AppstoreOutlined />,
      label: 'One-Day Tours',
    },
    {
      key: 'special-packages',
      icon: <StarOutlined />,
      label: 'Multi-Day Tours',
    },
     {
    key: 'testimonials',
    icon: <StarOutlined />,
    label: 'Testimonials',
  },
  {
    key: 'gallery',
    icon: <PictureOutlined />,
    label: 'Gallery',
  },
  ];

  const renderContent = () => {
    switch (selectedMenu) {
      case 'bookings':
        return <BookingsManagement active={selectedMenu}/>;
      case 'calendar':
        return <CalendarView active={selectedMenu}/>;
      case 'packages':
        return <PackagesManagement active={selectedMenu}/>;
      case 'special-packages':
        return <SpecialPackagesManagement active={selectedMenu}/>;
      case 'testimonials':
      return <TestimonialsManagement active={selectedMenu}/>;
      case 'gallery':
      return <GalleryManagement active={selectedMenu}/>;
      default:
        return <BookingsManagement active={selectedMenu}/>;
    }
  };

  const isMobile = !screens.lg;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Desktop Sider */}
      {!isMobile && (
        <Sider
          breakpoint="lg"
          collapsedWidth="0"
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
          }}
        >
          <div style={{
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.1)'
          }}>
            <Title level={4} style={{ color: 'white', margin: 0 }}>
              Admin Panel
            </Title>
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedMenu]}
            items={menuItems}
            onClick={(e) => handleMenuClick(e.key as MenuKey)}
          />
        </Sider>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title="Admin Panel"
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={250}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedMenu]}
          items={menuItems}
          onClick={(e) => handleMenuClick(e.key as MenuKey)}
        />
      </Drawer>

      <Layout style={{ marginLeft: isMobile ? 0 : 200 }}>
        <Header
          style={{
            padding: isMobile ? '0 16px' : '0 24px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuVisible(true)}
                style={{ fontSize: '18px' }}
              />
            )}
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              {isMobile ? 'Dashboard' : 'Tourist Guide Dashboard'}
            </Title>
          </div>
          <Button
            type="primary"
            danger
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : 'Logout'}
          </Button>
        </Header>
        <Content style={{
          margin: isMobile ? '16px 8px 0' : '24px 16px 0',
          overflow: 'initial'
        }}>
          <div style={{
            padding: isMobile ? 12 : 24,
            background: '#fff',
            minHeight: 360,
            borderRadius: '8px'
          }}>
            {renderContent()}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminDashboard;
